import React, { useState } from "react";
import {
  ArrowPathIcon,
  ClipboardDocumentIcon,
  LockClosedIcon,
  LockOpenIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { Link, useNavigate, useParams } from "react-router-dom";

import Layout from "../../components/layout/Layout";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Page,
  PageBody,
  PageHeader,
  Panel,
  PanelBody,
  PanelDescription,
  PanelHeader,
  PanelTitle,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from "../../components/ui";
import {
  PULSE_TRACKS,
  PulseTrack,
  PulseSubmission,
  PulseAnalysis,
  PulseCountSignal,
  useClosePulseWorkshop,
  useDeletePulseWorkshop,
  usePulseWorkshop,
  usePulseSubmissions,
  usePulseAnalysis,
  usePulseReport,
  useGeneratePulseReport,
  useDownloadPulseReport,
  useReopenPulseWorkshop,
  useRotatePulseWorkshopLink,
  useUpdatePulseSubmissionInclusion,
  useUpdatePulseWorkshop,
} from "../../hooks/usePulse";

function trackLabel(track: PulseTrack): string {
  return PULSE_TRACKS.find((option) => option.value === track)?.label ?? track;
}

function absolutePublicUrl(publicPath: string): string {
  return new URL(publicPath, window.location.origin).toString();
}

const SUBMISSION_PAGE_SIZE = 50;

type PulseWorkshopEditForm = {
  organization_name: string;
  workshop_date: string;
  tracks: PulseTrack[];
  referral_context: string;
  internal_notes: string;
};

export default function PulseWorkshopPage() {
  const { workshopId = "" } = useParams();
  const id = Number(workshopId);
  const navigate = useNavigate();
  const [submissionOffset, setSubmissionOffset] = useState(0);
  const workshopQuery = usePulseWorkshop(id);
  const closeWorkshop = useClosePulseWorkshop();
  const reopenWorkshop = useReopenPulseWorkshop();
  const rotateLink = useRotatePulseWorkshopLink();
  const deleteWorkshop = useDeletePulseWorkshop();
  const submissionsQuery = usePulseSubmissions(
    id,
    SUBMISSION_PAGE_SIZE,
    submissionOffset,
  );
  const updateSubmission = useUpdatePulseSubmissionInclusion(id);
  const updateWorkshop = useUpdatePulseWorkshop(id);
  const analysisQuery = usePulseAnalysis(id);
  const reportQuery = usePulseReport(id);
  const generateReport = useGeneratePulseReport(id);
  const downloadReport = useDownloadPulseReport(id);
  const [copied, setCopied] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [editForm, setEditForm] = useState<PulseWorkshopEditForm>({
    organization_name: "",
    workshop_date: "",
    tracks: [],
    referral_context: "",
    internal_notes: "",
  });

  const workshop = workshopQuery.data;
  const actionPending =
    closeWorkshop.isPending || reopenWorkshop.isPending || rotateLink.isPending;

  const copyLink = async () => {
    if (!workshop) return;
    await navigator.clipboard.writeText(
      absolutePublicUrl(workshop.public_path),
    );
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const removeWorkshop = async () => {
    if (!workshop || deleteConfirmation !== workshop.organization_name) return;
    await deleteWorkshop.mutateAsync(workshop.id);
    navigate("/pulse");
  };

  const openEdit = () => {
    if (!workshop) return;
    setEditForm({
      organization_name: workshop.organization_name,
      workshop_date: workshop.workshop_date,
      tracks: workshop.tracks,
      referral_context: workshop.referral_context ?? "",
      internal_notes: workshop.internal_notes ?? "",
    });
    setShowEdit(true);
  };

  const saveEdit = async (event: React.FormEvent) => {
    event.preventDefault();
    await updateWorkshop.mutateAsync(editForm);
    setShowEdit(false);
  };

  const toggleEditTrack = (track: PulseTrack, checked: boolean) => {
    setEditForm((current) => ({
      ...current,
      tracks: checked
        ? [...current.tracks, track]
        : current.tracks.filter((value) => value !== track),
    }));
  };

  const downloadReportHtml = async () => {
    if (!workshop) return;
    const blob = await downloadReport.mutateAsync();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = `pulse-${workshop.organization_slug}-${workshop.workshop_date}.html`;
    link.click();
    URL.revokeObjectURL(objectUrl);
  };

  const generateReportHtml = async () => {
    try {
      await generateReport.mutateAsync();
    } catch {
      // React Query retains the mutation error for the focused alert below.
    }
  };

  const openDelete = () => {
    setDeleteConfirmation("");
    setShowDelete(true);
  };

  const closeDelete = () => {
    setDeleteConfirmation("");
    setShowDelete(false);
  };

  return (
    <Layout>
      <Page maxWidth="xl">
        {workshopQuery.isLoading ? (
          <div className="flex min-h-96 items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : workshopQuery.isError || !workshop ? (
          <PageBody className="pt-8">
            <Alert variant="error">
              This PULSE workshop could not be loaded.
            </Alert>
          </PageBody>
        ) : (
          <>
            <PageHeader
              title={workshop.organization_name}
              description={`Codex workshop · ${new Intl.DateTimeFormat("en-US", { dateStyle: "long" }).format(new Date(`${workshop.workshop_date}T00:00:00`))}`}
              breadcrumb={
                <Link
                  className="text-sm text-eliza-red hover:underline"
                  to="/pulse"
                >
                  PULSE workshops
                </Link>
              }
              actions={
                <Badge
                  variant={workshop.status === "open" ? "success" : "secondary"}
                >
                  {workshop.status}
                </Badge>
              }
            />
            <PageBody className="space-y-5">
              <Tabs defaultValue="overview">
                <TabsList
                  variant="underline"
                  className="w-full overflow-x-auto"
                >
                  <TabsTrigger value="overview" variant="underline">
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="analysis" variant="underline">
                    Analysis
                  </TabsTrigger>
                  <TabsTrigger value="submissions" variant="underline">
                    Anonymous Submissions
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-5">
              <Panel>
                <PanelHeader>
                  <PanelTitle>Workshop overview</PanelTitle>
                      <PanelDescription>
                        Share the survey, manage availability, and keep
                        facilitator context together.
                      </PanelDescription>
                </PanelHeader>
                <PanelBody className="space-y-6">
                  <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Survey link
                        </div>
                    <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                          <Input
                            readOnly
                            value={absolutePublicUrl(workshop.public_path)}
                            aria-label="Survey link"
                          />
                          <Button
                            variant="secondary"
                            onClick={copyLink}
                            aria-label="Copy survey link"
                          >
                        <ClipboardDocumentIcon />
                            {copied ? "Copied" : "Copy link"}
                      </Button>
                    </div>
                        <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
                          Generated when this workshop is created. It uses this
                          environment&apos;s host, so local previews show localhost;
                          deployed workshops use the hosted ElizaPlatform URL.
                        </p>
                  </div>

                  <div className="grid gap-5 border-t border-gray-100 pt-5 dark:border-dark-border sm:grid-cols-2">
                    <div>
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Tracks
                          </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                            {workshop.tracks.map((track) => (
                              <Badge key={track} variant="outline">
                                {trackLabel(track)}
                              </Badge>
                            ))}
                      </div>
                    </div>
                    <div>
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Responses
                          </div>
                      <p className="mt-2 text-sm text-charcoal dark:text-gray-200">
                            {submissionsQuery.data?.included_count ?? 0}{" "}
                            included
                            {submissionsQuery.data?.excluded_count
                              ? ` · ${submissionsQuery.data.excluded_count} excluded`
                              : ""}
                      </p>
                    </div>
                  </div>

                  {workshop.referral_context && (
                    <div className="border-t border-gray-100 pt-5 dark:border-dark-border">
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            OpenAI referral context
                          </div>
                          <p className="mt-2 whitespace-pre-wrap text-sm text-charcoal dark:text-gray-200">
                            {workshop.referral_context}
                          </p>
                    </div>
                  )}
                  {workshop.internal_notes && (
                    <div className="border-t border-gray-100 pt-5 dark:border-dark-border">
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Internal notes
                          </div>
                          <p className="mt-2 whitespace-pre-wrap text-sm text-charcoal dark:text-gray-200">
                            {workshop.internal_notes}
                          </p>
                    </div>
                  )}
                </PanelBody>
              </Panel>

              {workshop.can_mutate && (
                <Panel>
                  <PanelHeader>
                    <PanelTitle>Workshop controls</PanelTitle>
                        <PanelDescription>
                          Only the workshop creator and platform admins can make
                          these changes.
                        </PanelDescription>
                  </PanelHeader>
                  <PanelBody className="flex flex-wrap gap-3">
                        <Button variant="secondary" onClick={openEdit}>
                          Edit setup
                        </Button>
                        {workshop.status === "open" ? (
                          <Button
                            variant="secondary"
                            disabled={actionPending}
                            onClick={() =>
                              closeWorkshop.mutateAsync(workshop.id)
                            }
                            aria-label="Close survey"
                          >
                        <LockClosedIcon />
                        Close survey
                      </Button>
                    ) : (
                          <Button
                            variant="secondary"
                            disabled={actionPending}
                            onClick={() =>
                              reopenWorkshop.mutateAsync(workshop.id)
                            }
                            aria-label="Reopen survey"
                          >
                        <LockOpenIcon />
                        Reopen survey
                      </Button>
                    )}
                        <Button
                          variant="secondary"
                          disabled={actionPending}
                          onClick={() => rotateLink.mutateAsync(workshop.id)}
                          aria-label="Rotate link"
                        >
                      <ArrowPathIcon />
                      Rotate link
                    </Button>
                        <Button
                          variant="destructive"
                          onClick={openDelete}
                        >
                      <TrashIcon />
                      Delete permanently
                    </Button>
                  </PanelBody>
                </Panel>
              )}
                </TabsContent>

                <TabsContent value="analysis">
                  <WorkshopAnalysis
                    analysis={analysisQuery.data}
                    isLoading={analysisQuery.isLoading || reportQuery.isLoading}
                    isError={analysisQuery.isError || reportQuery.isError}
                    generationError={generateReport.isError}
                    reportStatus={reportQuery.data?.status ?? "missing"}
                    canGenerate={workshop.can_mutate}
                    isGenerating={generateReport.isPending}
                    isDownloading={downloadReport.isPending}
                    onGenerate={generateReportHtml}
                    onDownload={downloadReportHtml}
                  />
                </TabsContent>

                <TabsContent value="submissions">
                  <AnonymousSubmissions
                    submissions={submissionsQuery.data?.items ?? []}
                    includedCount={submissionsQuery.data?.included_count ?? 0}
                    excludedCount={submissionsQuery.data?.excluded_count ?? 0}
                    total={submissionsQuery.data?.total ?? 0}
                    limit={SUBMISSION_PAGE_SIZE}
                    offset={submissionOffset}
                    isLoading={submissionsQuery.isLoading}
                    isError={submissionsQuery.isError}
                    canMutate={workshop.can_mutate}
                    isMutating={updateSubmission.isPending}
                    onInclusionChange={(submissionId, included) =>
                      updateSubmission.mutateAsync({ submissionId, included })
                    }
                    onPageChange={setSubmissionOffset}
                  />
                </TabsContent>
              </Tabs>
            </PageBody>
          </>
        )}
      </Page>

      {workshop && (
        <Modal open={showEdit} onClose={() => setShowEdit(false)}>
          <ModalContent size="lg">
            <form onSubmit={saveEdit}>
              <ModalHeader>
                <ModalTitle>Edit workshop setup</ModalTitle>
                <ModalDescription>
                  The public company slug remains frozen; other setup fields
                  update immediately.
                </ModalDescription>
              </ModalHeader>
              <ModalBody className="space-y-5">
                {updateWorkshop.isError && (
                  <Alert variant="error">
                    The workshop setup could not be updated.
                  </Alert>
                )}
                <div className="space-y-2">
                  <Label htmlFor="pulse-edit-organization">Organization</Label>
                  <Input
                    id="pulse-edit-organization"
                    required
                    value={editForm.organization_name}
                    onChange={(event) =>
                      setEditForm((current) => ({
                        ...current,
                        organization_name: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pulse-edit-date">Workshop date</Label>
                  <Input
                    id="pulse-edit-date"
                    type="date"
                    required
                    value={editForm.workshop_date}
                    onChange={(event) =>
                      setEditForm((current) => ({
                        ...current,
                        workshop_date: event.target.value,
                      }))
                    }
                  />
                </div>
                <fieldset className="space-y-3">
                  <legend className="text-sm font-medium">
                    Tracks offered
                  </legend>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {PULSE_TRACKS.map((track) => (
                      <Checkbox
                        key={track.value}
                        label={track.label}
                        checked={editForm.tracks.includes(track.value)}
                        onChange={(event) =>
                          toggleEditTrack(track.value, event.target.checked)
                        }
                      />
                    ))}
                  </div>
                </fieldset>
                <div className="space-y-2">
                  <Label htmlFor="pulse-edit-referral">
                    OpenAI referral context
                  </Label>
                  <Textarea
                    id="pulse-edit-referral"
                    value={editForm.referral_context ?? ""}
                    onChange={(event) =>
                      setEditForm((current) => ({
                        ...current,
                        referral_context: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pulse-edit-notes">Internal notes</Label>
                  <Textarea
                    id="pulse-edit-notes"
                    value={editForm.internal_notes ?? ""}
                    onChange={(event) =>
                      setEditForm((current) => ({
                        ...current,
                        internal_notes: event.target.value,
                      }))
                    }
                  />
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowEdit(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="brand"
                  disabled={
                    !editForm.organization_name.trim() ||
                    !editForm.workshop_date ||
                    !editForm.tracks.length ||
                    updateWorkshop.isPending
                  }
                >
                  Save setup
                </Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Modal>
      )}

      {workshop && (
        <Modal open={showDelete} onClose={closeDelete}>
          <ModalContent size="md">
            <ModalHeader>
              <ModalTitle>Delete workshop permanently?</ModalTitle>
              <ModalDescription>
                This will delete the workshop, every anonymous submission, and
                its report. It cannot be undone.
              </ModalDescription>
            </ModalHeader>
            <ModalBody className="space-y-3">
              <Alert variant="error">
                Type {workshop.organization_name} to confirm permanent deletion.
              </Alert>
              <Input
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                aria-label="Organization name confirmation"
              />
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" onClick={closeDelete}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={
                  deleteConfirmation !== workshop.organization_name ||
                  deleteWorkshop.isPending
                }
                onClick={removeWorkshop}
              >
                Delete permanently
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Layout>
  );
}

function humanize(value: string): string {
  return value
    .replace(/-/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function optionalBooleanLabel(value?: boolean | null): string {
  if (value == null) return "Not asked";
  return value ? "Yes" : "Not yet";
}

function EvidenceList({
  title,
  signals,
}: {
  title: string;
  signals: PulseCountSignal[];
}) {
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>{title}</PanelTitle>
      </PanelHeader>
      <PanelBody>
        {signals.length ? (
          <div className="space-y-3">
            {signals.slice(0, 6).map((signal) => (
              <div
                key={signal.key}
                className="flex items-center justify-between gap-4 text-sm"
              >
                <span>{humanize(signal.key)}</span>
                <strong>
                  {signal.count}/{signal.denominator} · {signal.percentage}%
                </strong>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No signal reported.</p>
        )}
      </PanelBody>
    </Panel>
  );
}

function WorkshopAnalysis({
  analysis,
  isLoading,
  isError,
  generationError,
  reportStatus,
  canGenerate,
  isGenerating,
  isDownloading,
  onGenerate,
  onDownload,
}: {
  analysis?: PulseAnalysis;
  isLoading: boolean;
  isError: boolean;
  generationError: boolean;
  reportStatus: "missing" | "current" | "stale";
  canGenerate: boolean;
  isGenerating: boolean;
  isDownloading: boolean;
  onGenerate: () => Promise<unknown>;
  onDownload: () => Promise<void>;
}) {
  if (isLoading)
    return (
      <div className="flex min-h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  if (isError || !analysis)
    return (
      <Alert variant="error">Workshop intelligence could not be loaded.</Alert>
    );

  return (
    <div className="space-y-5">
      {generationError && (
        <Alert variant="error">
          The brief could not be generated. The current analysis is unchanged;
          try again.
        </Alert>
      )}
      <Panel>
        <PanelHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <PanelTitle>Workshop intelligence</PanelTitle>
              <PanelDescription>
                Live deterministic analysis over included anonymous submissions.
              </PanelDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant={
                  reportStatus === "current"
                    ? "success"
                    : reportStatus === "stale"
                      ? "warning"
                      : "secondary"
                }
              >
                Brief: {reportStatus}
              </Badge>
              {reportStatus !== "missing" && (
                <Button
                  variant="secondary"
                  disabled={isDownloading}
                  onClick={onDownload}
                >
                  Download HTML
                </Button>
              )}
              {canGenerate && (
                <Button
                  variant="brand"
                  disabled={!analysis.submission_count || isGenerating}
                  onClick={onGenerate}
                  aria-label={
                    reportStatus === "missing"
                      ? "Generate brief"
                      : "Regenerate brief"
                  }
                >
                  {reportStatus === "missing"
                    ? "Generate brief"
                    : "Regenerate brief"}
                </Button>
              )}
            </div>
          </div>
        </PanelHeader>
        <PanelBody className="space-y-5">
          {reportStatus === "stale" && (
            <Alert variant="warning">
              The stored brief is stale because responses or inclusion state
              changed. Regenerate it before facilitation.
            </Alert>
          )}
          {analysis.directional && (
            <Alert variant="warning">
              Directional evidence: this read is based on one or two included
              submissions.
            </Alert>
          )}
          <p className="text-base leading-7 text-charcoal dark:text-gray-200">
            {analysis.executive_read}
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl bg-gray-50 p-4 dark:bg-dark-surface-2">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Included
              </div>
              <strong className="mt-1 block text-2xl">
                {analysis.submission_count}
              </strong>
            </div>
            <div className="rounded-xl bg-gray-50 p-4 dark:bg-dark-surface-2">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Excluded
              </div>
              <strong className="mt-1 block text-2xl">
                {analysis.excluded_submission_count}
              </strong>
            </div>
            <div className="rounded-xl bg-gray-50 p-4 dark:bg-dark-surface-2">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Roles received
              </div>
              <strong className="mt-1 block text-2xl">
                {Object.keys(analysis.role_counts).length}
              </strong>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Submitted roles
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(analysis.role_counts).map(([key, count]) => (
                  <Badge key={key} variant="outline">
                    {humanize(key)}: {count}
                  </Badge>
                ))}
          </div>
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Perspectives
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(analysis.perspective_counts).map(
                  ([key, count]) => (
                    <Badge key={key} variant="outline">
                      {humanize(key)}: {count}
                    </Badge>
                  ),
                )}
              </div>
            </div>
          </div>
          {analysis.caveats.length > 0 && (
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-600 dark:text-gray-300">
              {analysis.caveats.map((caveat) => (
                <li key={caveat}>{caveat}</li>
              ))}
            </ul>
          )}
        </PanelBody>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-3">
        <EvidenceList title="Current friction" signals={analysis.friction} />
        <EvidenceList title="Workshop wishlist" signals={analysis.priorities} />
        <EvidenceList title="Readiness blockers" signals={analysis.blockers} />
      </div>

      <Panel>
        <PanelHeader>
          <PanelTitle>Capability maturity</PanelTitle>
          <PanelDescription>
            Averages retain their eligible evidence counts; split distributions
            are flagged.
          </PanelDescription>
        </PanelHeader>
        <PanelBody className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {analysis.maturity.map((signal) => (
            <div
              key={signal.key}
              className="rounded-xl border border-gray-200 p-4 dark:border-dark-border"
            >
              <div className="flex items-start justify-between gap-3">
                <strong className="text-sm">{humanize(signal.key)}</strong>
                {signal.high_variance && (
                  <Badge variant="warning">High variance</Badge>
                )}
              </div>
              <div className="mt-3 text-2xl font-semibold">
                {signal.average.toFixed(2)}{" "}
                <span className="text-sm font-normal text-gray-500">/ 4</span>
              </div>
              <div className="mt-1 text-xs text-gray-500">
                Median {signal.median.toFixed(2)} · n={signal.count}
              </div>
              <div
                className="mt-3 flex gap-1"
                aria-label={`${humanize(signal.key)} stage distribution`}
              >
                {Object.entries(signal.distribution).map(([stage, count]) => (
                  <span
                    key={stage}
                    className="flex-1 rounded bg-gray-100 px-1 py-1 text-center text-[10px] dark:bg-dark-surface-2"
                  >
                    {stage}: {count}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </PanelBody>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel>
          <PanelHeader>
            <PanelTitle>Perspective discrepancies</PanelTitle>
            <PanelDescription>
              Differences of at least one maturity stage.
            </PanelDescription>
          </PanelHeader>
          <PanelBody className="space-y-3">
            {analysis.perspective_discrepancies.length ? (
              analysis.perspective_discrepancies.map((item) => (
                <div
                  key={item.key}
                  className="rounded-xl border border-gray-200 p-4 text-sm dark:border-dark-border"
                >
                  <strong>{humanize(item.key)}</strong>
                  <p className="mt-1 text-gray-500">
                    Individual {item.individual_average.toFixed(2)}/4 (n=
                    {item.individual_count}) · Team{" "}
                    {item.team_average.toFixed(2)}/4 (n={item.team_count}) ·
                    Difference {item.difference.toFixed(2)}
                  </p>
            </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">
                No one-stage perspective discrepancies detected.
              </p>
            )}
          </PanelBody>
        </Panel>
        <Panel>
          <PanelHeader>
            <PanelTitle>Questions to validate live</PanelTitle>
            <PanelDescription>
              Use the session to recover nuance that a short survey cannot
              capture.
            </PanelDescription>
          </PanelHeader>
          <PanelBody>
            <ol className="list-decimal space-y-2 pl-5 text-sm">
              {analysis.validation_questions.map((question) => (
                <li key={question}>{question}</li>
          ))}
            </ol>
        </PanelBody>
      </Panel>
      </div>

      <Panel>
        <PanelHeader>
          <PanelTitle>Recommended workshop modules</PanelTitle>
          <PanelDescription>
            Ordered by deterministic disposition, demand, and stable module key.
          </PanelDescription>
        </PanelHeader>
        <PanelBody className="space-y-3">
          {analysis.recommendations
            .filter((item) => item.demand >= 0.25)
            .slice(0, 6)
            .map((item) => (
              <div
                key={item.module_key}
                className="flex flex-col justify-between gap-3 rounded-xl border border-gray-200 p-4 dark:border-dark-border sm:flex-row sm:items-center"
              >
                <div>
                  <strong>{item.label}</strong>
                  <div className="mt-1 text-xs text-gray-500">
                    Demand {(item.demand * 100).toFixed(0)}% · friction{" "}
                    {item.friction_count}/{item.eligible_count} · interest{" "}
                    {item.interest_count}/{item.eligible_count} · maturity{" "}
                    {item.maturity.toFixed(2)}/4 · {humanize(item.disposition)}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Threshold: {item.threshold_result} · {item.evidence.join(" · ")}
                  </div>
                </div>
              <Badge variant="outline">{item.suggested_minutes} min</Badge>
            </div>
          ))}
          {!analysis.recommendations.some((item) => item.demand >= 0.25) && (
            <p className="text-sm text-gray-500">
              No module crossed the 25% demand threshold yet.
            </p>
          )}
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHeader>
          <PanelTitle>Draft agenda</PanelTitle>
          <PanelDescription>
            Draft — facilitator review required.
          </PanelDescription>
        </PanelHeader>
        <PanelBody className="space-y-4">
          {analysis.agenda.map((block) => (
            <div
              key={block.key}
              className="grid gap-2 border-b border-gray-100 pb-4 last:border-0 dark:border-dark-border sm:grid-cols-[80px_1fr]"
            >
              <strong className="text-eliza-red">{block.minutes} min</strong>
              <div>
                <strong>{block.title}</strong>
                <p className="mt-1 text-xs text-gray-500">
                  {block.format} · {block.evidence}
                </p>
              </div>
            </div>
          ))}
        </PanelBody>
      </Panel>
    </div>
  );
}

function SubmissionSignals({
  label,
  values,
}: {
  label: string;
  values: string[];
}) {
  if (!values.length) return null;
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {values.map((value) => (
          <Badge key={value} variant="outline">
            {humanize(value)}
          </Badge>
        ))}
      </div>
    </div>
  );
}

export function AnonymousSubmissions({
  submissions,
  includedCount,
  excludedCount,
  total,
  limit,
  offset,
  isLoading,
  isError,
  canMutate,
  isMutating,
  onInclusionChange,
  onPageChange,
}: {
  submissions: PulseSubmission[];
  includedCount: number;
  excludedCount: number;
  total: number;
  limit: number;
  offset: number;
  isLoading: boolean;
  isError: boolean;
  canMutate: boolean;
  isMutating: boolean;
  onInclusionChange: (
    submissionId: number,
    included: boolean,
  ) => Promise<unknown>;
  onPageChange: (offset: number) => void;
}) {
  if (isLoading)
    return (
      <div className="flex min-h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  if (isError)
    return (
      <Alert variant="error">Anonymous submissions could not be loaded.</Alert>
    );
  if (!submissions.length) {
    return (
      <Panel>
        <PanelBody className="py-12 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No anonymous submissions yet.
          </p>
        </PanelBody>
      </Panel>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {includedCount} included · {excludedCount} excluded
        </p>
        {!canMutate && (
          <p className="text-xs text-gray-500">
            Only the workshop creator or a platform admin can change inclusion.
          </p>
        )}
      </div>
      {submissions.map((submission, index) => (
        <Panel
          key={submission.id}
          className={submission.included ? "" : "opacity-70"}
        >
          <PanelHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <PanelTitle>Submission {total - offset - index}</PanelTitle>
                <PanelDescription>
                  {humanize(submission.role)}
                  {submission.job_title ? ` · ${submission.job_title}` : ""}
                  {` · ${humanize(submission.submission_perspective)}`}
                  {` · ${new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(new Date(submission.created_at))}`}
                </PanelDescription>
              </div>
              <Badge variant={submission.included ? "success" : "secondary"}>
                {submission.included ? "Included" : "Excluded"}
              </Badge>
            </div>
          </PanelHeader>
          <PanelBody className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <SubmissionSignals
                label="Current friction"
                values={submission.answers.friction}
              />
              <SubmissionSignals
                label="Workshop wishlist"
                values={submission.answers.priorities}
              />
              <SubmissionSignals
                label="Constraints"
                values={submission.answers.blockers}
              />
              <SubmissionSignals
                label="Current tools"
                values={submission.answers.current_tools}
              />
            </div>
            <div className="grid gap-3 border-t border-gray-100 pt-5 text-sm dark:border-dark-border sm:grid-cols-2">
              <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-dark-surface-2">
                <span className="text-gray-500">Overall AI usage</span>
                <strong className="float-right">
                  {submission.answers.ai_usage_stage} / 4
                </strong>
              </div>
              {submission.answers.skill_profile && (
                <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-dark-surface-2">
                  <span className="text-gray-500">Skills built</span>
                  <strong className="float-right">
                    {submission.answers.skill_profile.count_bucket}
                  </strong>
                  <div className="mt-2 clear-both text-xs text-gray-500">
                    Nested:{" "}
                    {optionalBooleanLabel(
                      submission.answers.skill_profile.has_nested_skills,
                    )}{" "}
                    · In automations:{" "}
                    {optionalBooleanLabel(
                      submission.answers.skill_profile.used_in_automations,
                    )}{" "}
                    · Team shared:{" "}
                    {optionalBooleanLabel(
                      submission.answers.skill_profile.shared_across_team,
                    )}{" "}
                    · Evaluated:{" "}
                    {optionalBooleanLabel(
                      submission.answers.skill_profile.repeatably_evaluated,
                    )}
                  </div>
                </div>
              )}
            </div>
            {submission.answers.leader_context && (
              <div className="border-t border-gray-100 pt-5 dark:border-dark-border">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Leadership operating context
                </div>
                <div className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <span className="text-gray-500">Team adoption:</span>{" "}
                    {humanize(
                      submission.answers.leader_context.team_adoption_pattern,
                    )}
                  </div>
                  <div>
                    <span className="text-gray-500">Repository standards:</span>{" "}
                    {humanize(
                      submission.answers.leader_context
                        .repository_standardization,
                    )}
                  </div>
                  <div>
                    <span className="text-gray-500">Review governance:</span>{" "}
                    {humanize(
                      submission.answers.leader_context.review_governance,
                    )}
                  </div>
                  <SubmissionSignals
                    label="Leadership emphasis"
                    values={
                      submission.answers.leader_context.leadership_emphasis
                    }
                  />
                  <SubmissionSignals
                    label="Approved-tooling constraints"
                    values={
                      submission.answers.leader_context
                        .approved_tooling_constraints ?? []
                    }
                  />
                </div>
              </div>
            )}
            {(submission.answers.other_friction ||
              submission.answers.other_readiness_context) && (
              <div className="grid gap-5 border-t border-gray-100 pt-5 dark:border-dark-border sm:grid-cols-2">
                {submission.answers.other_friction && (
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Additional friction
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-charcoal dark:text-gray-200">
                      {submission.answers.other_friction}
                    </p>
                  </div>
                )}
                {submission.answers.other_readiness_context && (
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Additional readiness context
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-charcoal dark:text-gray-200">
                      {submission.answers.other_readiness_context}
                    </p>
                  </div>
                )}
              </div>
            )}
            {submission.answers.workflow_example && (
              <div className="border-t border-gray-100 pt-5 dark:border-dark-border">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Workflow example
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-charcoal dark:text-gray-200">
                  {submission.answers.workflow_example}
                </p>
              </div>
            )}
            <div className="border-t border-gray-100 pt-5 dark:border-dark-border">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Capability stages
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries(submission.answers.maturity).map(
                  ([key, value]) => (
                    <div
                      key={key}
                      className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-xs dark:bg-dark-surface-2"
                    >
                      <span>{humanize(key)}</span>
                      <strong>{value} / 4</strong>
                  </div>
                  ),
                )}
              </div>
            </div>
            {!submission.included && (
              <Alert variant="warning">
                Excluded from analysis by{" "}
                {submission.excluded_by_user_id
                  ? `User #${submission.excluded_by_user_id}`
                  : "an unavailable user"}{" "}
                {submission.excluded_at
                  ? `on ${new Intl.DateTimeFormat("en-US", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    }).format(new Date(submission.excluded_at))}`
                  : "at an unavailable time"}
                {submission.exclusion_reason
                  ? ` · ${submission.exclusion_reason}`
                  : ""}
              </Alert>
            )}
            {canMutate && (
              <div className="flex justify-end border-t border-gray-100 pt-4 dark:border-dark-border">
                <Button
                  variant="secondary"
                  disabled={isMutating}
                  aria-label={
                    submission.included
                      ? "Exclude from analysis"
                      : "Include in analysis"
                  }
                  onClick={() =>
                    onInclusionChange(submission.id, !submission.included)
                  }
                >
                  {submission.included
                    ? "Exclude from analysis"
                    : "Include in analysis"}
                </Button>
              </div>
            )}
          </PanelBody>
        </Panel>
      ))}
      <div className="flex items-center justify-between gap-4">
        <span className="text-xs text-gray-500">
          Showing {offset + 1}–{offset + submissions.length} of {total}
        </span>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => onPageChange(Math.max(0, offset - limit))}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={offset + submissions.length >= total}
            onClick={() => onPageChange(offset + limit)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
